import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useInterval } from 'react-use';

import { Flex, Link } from '@gravity-ui/uikit';
import clx from 'classnames';
import { cancelSkillReview, devicesTestingApi, requestSkillDeploy, requestSkillReview } from 'client/api';
import { AliceAppActionButton } from 'client/blocks/AliceAppActionButton/AliceAppActionButton';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { SmartHomeTestingDate } from 'client/blocks/SmartHomeTestingDate/SmartHomeTestingDate';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { useSkillActionsWrapper } from 'client/hooks/useSkillActionsWrapper';
import { i18nWrapper } from 'client/lib/utils';
import { getDraftStatusHint } from 'client/locale/ru/draftStatus';
import { smartHomeDescriptionsAiredKeys, smartHomeDescriptionsKeys } from 'client/locale/ru/skillDescriptions';
import { getSmartHomeTestingChecklistTexts, smartHomeTestingTitleText } from 'client/locale/ru/smartHomeTesting';
import { DevicesTestingStatusMeta } from 'client/model/devicesTestingForm';
import { DraftStatus } from 'client/model/draft';
import { OperationType } from 'client/model/operation';
import { SkillAccess } from 'client/model/settings';
import { Channel, SmartHome } from 'client/model/skill';
import { getCanEditApp } from 'client/utils';
import {
    checkIfCancelled,
    getActionButtons,
    getModerationStatuses,
    getSlaTimings,
    splitModerationStatusesByCurrentStatus,
} from 'client/utils/draft-actions';
import { createOperation } from 'client/utils/operations';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeModeration.module.scss';
import { SmartHomeModerationItem } from './SmartHomeModerationItem';
import { SmartHomeValidationStatusesList } from './SmartHomeValidationStatuses';

interface Props {
    aliceApp: SmartHome;
    safeToRequestReview: boolean;
    afterReviewRequest: () => void;
    refreshAliceApp: () => void;
}

const t = i18nWrapper(keyset);

const getDescription = (onAir: boolean, status: DraftStatus) => {
    if (onAir) {
        return t(smartHomeDescriptionsAiredKeys[status]);
    }

    return t(smartHomeDescriptionsKeys[status]);
};

export const SmartHomeModeration: React.FC<Props> = ({
    aliceApp,
    safeToRequestReview,
    afterReviewRequest,
    refreshAliceApp,
}) => {
    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);

    const { operations, addOperation } = useContext(CurrentAliceAppContext)!;
    const { wrapper, error, hasPendingAction, statuses } = useSkillActionsWrapper();
    const [devicesTestingStatusMeta, setDevicesTestingStatusMeta] = useState<DevicesTestingStatusMeta>();

    const draftStatusHint = useMemo(() => getDraftStatusHint(), []);

    const isAllowedForDeploy = aliceApp.draft.isAllowedForDeploy;

    const disabled = !getCanEditApp(aliceApp);

    const handler = useCallback(() => {
        return devicesTestingApi.getDevicesTestingRecordStatus(aliceApp.id, aliceApp.channel);
    }, [aliceApp.id, aliceApp.channel]);

    const [loadStatusHandler, state] = useFetchingHandler({ handler });

    const loadStatus = useCallback(async () => {
        setDevicesTestingStatusMeta(await loadStatusHandler());
    }, [loadStatusHandler]);

    const loadStatusUnhandled = useCallback(async () => {
        setDevicesTestingStatusMeta(await handler());
    }, [handler]);

    useEffect(() => {
        void loadStatus();
    }, [loadStatus]);
    useInterval(loadStatusUnhandled, 1000 * 60);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cancelReview = useCallback(
        wrapper(cancelSkillReview, {
            onDone: () => {
                addOperation(createOperation({ aliceApp, type: OperationType.SkillWithdrawn }));
                refreshAliceApp();
            },
        }),
        [addOperation, wrapper],
    );

    const requestDeploy = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(t('unsaved-alert'));

        if (!changesDiscarded) {
            return;
        }

        const requestDeployFunc = requestSkillDeploy;

        const action = wrapper(requestDeployFunc, {
            onDone: () => {
                addOperation(createOperation({ aliceApp, type: OperationType.DeployRequested }));
                refreshAliceApp();
            },
        });

        await action();
    }, [safeToRequestReview, addOperation, aliceApp, wrapper, refreshAliceApp]);

    const onConfirm = async () => {
        const action = wrapper(requestSkillReview, {
            onDone: () => {
                addOperation(createOperation({ aliceApp, type: OperationType.ReviewRequested }));
                refreshAliceApp();
            },
        });
        await action();

        afterReviewRequest();
        setConfirmModalIsOpen(false);
    };

    const requestReview = () => {
        const changesDiscarded = safeToRequestReview || window.confirm(t('unsaved-alert'));

        if (!changesDiscarded) {
            return;
        }

        setConfirmModalIsOpen(true);
    };

    if (!operations.length) {
        return <PageLoader />;
    }

    const actions = {
        cancelReview: cancelReview,
        requestDeploy: requestDeploy,
        requestReview: requestReview,
    };

    const { status, channel } = aliceApp.draft;

    const moderationStatuses = getModerationStatuses(aliceApp) as Array<DraftStatus>;

    const { cancellationComment, cancelledOnStatus } = checkIfCancelled(operations, channel) ?? {};
    const isCancelled = Boolean(cancelledOnStatus);

    const lastNonCancelledStatus = cancelledOnStatus ?? status;

    const { moderationStatusesBeforeCurrent, moderationStatusesAfterCurrent } = splitModerationStatusesByCurrentStatus(
        moderationStatuses,
        lastNonCancelledStatus,
    );

    const isPrivate = aliceApp.draft.skillAccess === SkillAccess.Private;

    const isMeetingApproval = channel === Channel.SmartHome && status === DraftStatus.MeetingApproval;
    const isTesting =
        channel === Channel.SmartHome &&
        (status === DraftStatus.WaitingForTesting || status === DraftStatus.DevicesTesting);
    const zoomLink =
        (isTesting &&
            devicesTestingStatusMeta &&
            devicesTestingStatusMeta.status !== 'NOT_SPECIFIED' &&
            devicesTestingStatusMeta.record.type === 'zoom' &&
            devicesTestingStatusMeta.record.options.zoomLink) ||
        null;

    return (
        <>
            <div className={styles.smartHomeModeration__container}>
                {moderationStatusesBeforeCurrent.map((draftStatus, i) => (
                    <div className={styles.smartHomeModeration__item} key={i}>
                        {draftStatus === DraftStatus.MeetingApproval && !isPrivate ? (
                            <SmartHomeModerationItem
                                draftStatus={draftStatus}
                                state="completed"
                                position={i + 1}
                                showSuccessText={false}
                            >
                                <SmartHomeTestingDate
                                    aliceApp={aliceApp}
                                    state={state}
                                    loadStatusUnhandled={loadStatusUnhandled}
                                    devicesTestingStatusMeta={devicesTestingStatusMeta}
                                    disabled
                                />
                            </SmartHomeModerationItem>
                        ) : (
                            <SmartHomeModerationItem draftStatus={draftStatus} state="completed" position={i + 1} />
                        )}
                    </div>
                ))}
                <div className={styles.smartHomeModeration__item}>
                    <SmartHomeModerationItem
                        draftStatus={lastNonCancelledStatus}
                        state={isCancelled ? 'rejected' : 'current'}
                        position={moderationStatusesBeforeCurrent.length + 1}
                        key={moderationStatusesBeforeCurrent.length}
                    >
                        {isMeetingApproval && !isPrivate ? (
                            <SmartHomeTestingDate
                                aliceApp={aliceApp}
                                state={state}
                                loadStatusUnhandled={loadStatusUnhandled}
                                devicesTestingStatusMeta={devicesTestingStatusMeta}
                                disabled={disabled}
                            />
                        ) : !isCancelled ? (
                            <>
                                <div className={styles.smartHomeModerationLabel}>
                                    {getDescription(aliceApp.onAir, status)}
                                </div>
                                {!isPrivate && !isAllowedForDeploy && draftStatusHint[status] ? (
                                    <div className={styles.smartHomeModerationLabel}>{draftStatusHint[status]}</div>
                                ) : null}
                                <div className={styles.smartHomeModerationLabel}>
                                    {getSlaTimings('current', channel, lastNonCancelledStatus)}
                                </div>
                            </>
                        ) : (
                            <div className={styles.smartHomeModerationLabel}>{cancellationComment}</div>
                        )}
                        {zoomLink && (
                            <div className={styles.smartHomeModeration__zoomLinkWrapper}>
                                <div className={styles.smartHomeModeration__zoomLink}>{t('meeting-zoom-link')}</div>
                                <Link href={zoomLink} target="_blank">
                                    {zoomLink}
                                </Link>
                            </div>
                        )}
                        <div>
                            <Flex gap={2}>
                                {getActionButtons(aliceApp).map((button) => {
                                    const disabledInvalid = button.settingsShouldBeValid && !isAllowedForDeploy;

                                    return (
                                        <div key={button.action}>
                                            <AliceAppActionButton
                                                action={actions[button.action]}
                                                disabled={hasPendingAction || disabledInvalid || disabled}
                                                view={button.theme}
                                                successLabel={button.action === 'requestDeploy' ? t('published') : ''}
                                            >
                                                {button.getText(isCancelled)}
                                            </AliceAppActionButton>
                                            {disabledInvalid && button.invalidSettingsMessage && (
                                                <div className={clx(styles.label, styles.label_warning)}>
                                                    {button.invalidSettingsMessage}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </Flex>
                            {error && <div className={clx(styles.label, styles.label_error)}>{error}</div>}
                            {statuses && <SmartHomeValidationStatusesList statuses={statuses} />}
                        </div>
                    </SmartHomeModerationItem>
                </div>
                {moderationStatusesAfterCurrent.map((status, i) => {
                    const initialIndex = moderationStatusesBeforeCurrent.length + i + 1;

                    return (
                        <div className={styles.smartHomeModeration__item} key={initialIndex}>
                            <SmartHomeModerationItem
                                draftStatus={status}
                                state="not-started"
                                position={initialIndex + 1}
                                slaTimingsText={getSlaTimings('not-started', channel, status)}
                            />
                        </div>
                    );
                })}
            </div>
            {confirmModalIsOpen && (
                <ModalWithConfirmation
                    confirmButtonLabel={t('continue')}
                    title={t('sh-precheck')}
                    onConfirm={onConfirm}
                    onClose={() => setConfirmModalIsOpen(false)}
                    loading={hasPendingAction}
                >
                    <>
                        <p className={styles.smartHomeConfirmModalChecklist__title}>{smartHomeTestingTitleText}</p>
                        <ul className={styles.smartHomeConfirmModalChecklist__list}>
                            {getSmartHomeTestingChecklistTexts().map((rule, i) => (
                                <li className={styles.smartHomeConfirmModalChecklist__item} key={i}>
                                    {rule}
                                </li>
                            ))}
                        </ul>
                    </>
                </ModalWithConfirmation>
            )}
        </>
    );
};
